<!-- Clinical_Results_List.vue -->
<template>
  <div class="flex flex-col">
    <!-- Filter Bar -->
    <div class="px-4 py-3 border-b border-gray-200 bg-white">
      <div class="flex flex-wrap items-center gap-3">
        <!-- Contract Filter (multi-select dropdown) -->
        <div class="relative" ref="contractDropdown">
          <button
            @click="contractDropdownOpen = !contractDropdownOpen"
            class="px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-blue-dark focus:ring-offset-4 flex items-center gap-1.5 min-w-[140px]"
          >
            <span v-if="filters.contractIds.length === 0" class="text-gray-500">All Contracts</span>
            <span v-else-if="filters.contractIds.length === 1">{{ filters.contractIds[0] }}</span>
            <span v-else>{{ filters.contractIds.length }} contracts</span>
            <FontAwesomeIcon :icon="['fas', 'chevron-down']" class="text-gray-400 text-xs ml-auto" />
          </button>
          <div
            v-if="contractDropdownOpen"
            class="absolute z-50 mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto min-w-[180px]"
          >
            <div
              class="px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer border-b border-gray-200 text-gray-500"
              @click="filters.contractIds = []; contractDropdownOpen = false; refreshGrid()"
            >
              All Contracts
            </div>
            <label
              v-for="contract in contractOptions"
              :key="contract.value"
              class="flex items-center px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer"
            >
              <input
                type="checkbox"
                :value="contract.value"
                v-model="filters.contractIds"
                @change="refreshGrid"
                class="mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              {{ contract.label }}
            </label>
          </div>
        </div>

        <!-- Measure Filter -->
        <select
          v-model="filters.measure"
          @change="refreshGrid"
          class="px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-blue-dark focus:ring-offset-4"
        >
          <option :value="null">All Measures</option>
          <option value="CCS">Cervical Cancer (CCS)</option>
          <option value="CHL">Chlamydia (CHL)</option>
          <option value="CRC">Colorectal (CRC)</option>
          <option value="GSD">HbA1c (GSD)</option>
          <option value="HIV">HIV Viral Load</option>
          <option value="KED">Kidney Eval (KED)</option>
        </select>

        <!-- Source Filter -->
        <select
          v-model="filters.source"
          @change="refreshGrid"
          class="px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-blue-dark focus:ring-offset-4"
        >
          <option :value="null">All Sources</option>
          <option value="ATHENA_EHR">Athena</option>
          <option value="HEALTHJUMP_LAB">HealthJump</option>
          <option value="LABCORP">LabCorp</option>
          <option value="QUEST">Quest</option>
        </select>

        <!-- Test Filter -->
        <select
          v-model="filters.test"
          @change="refreshGrid"
          class="px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-blue-dark focus:ring-offset-4 max-w-xs truncate"
        >
          <option :value="null">All Tests</option>
          <option v-for="test in testOptions" :key="test" :value="test">
            {{ test }}
          </option>
        </select>

        <!-- Search -->
        <div class="relative">
          <input
            v-model="filters.search" data-demo-search
            @keyup.enter="refreshGrid"
            type="text"
            placeholder="Search patient name..."
            class="sensitive pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-dark focus:ring-offset-4 w-48"
          />
          <FontAwesomeIcon
            :icon="['fas', 'search']"
            class="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs"
          />
        </div>

        <button
          @click="refreshGrid"
          class="btn-primary-sm"
        >
          Search
        </button>

        <!-- Stats -->
        <div class="ml-auto text-sm text-gray-500">
          {{ stats.totalResults }} results
        </div>
      </div>
    </div>

    <!-- Toolbar -->
    <div class="px-4 py-3 border-b border-gray-200 bg-white flex items-center justify-between">
      <h2 class="text-lg font-semibold text-gray-900">Clinical Results Finder</h2>
      <ExportMenu :exports="[{ type: 'csv', action: exportData }]" />
    </div>

    <!-- Main Content Area -->
    <div class="flex-1 flex overflow-hidden">
      <!-- Left: AG-Grid List -->
      <div
        :style="{ width: selectedPatient ? '50%' : '100%' }"
        class="transition-all duration-300 flex flex-col border-r border-gray-200"
        style="height: calc(100vh - 250px)"
      >
        <AgGridVue
          :key="tableKey"
          :theme="gridTheme"
          class="flex-1 w-full"
          :column-defs="columnDefs"
          :default-col-def="defaultColDef"
          :grid-options="gridOptions"
          :loading="loading"
          :row-height="56"
          :header-height="48"
          :row-model-type="'serverSide'"
          :get-row-id="getRowId"
          :row-class-rules="rowClassRules"
          @grid-ready="onGridReady"
          @row-clicked="onRowClicked"
        />

        <!-- Footer -->
        <div class="px-4 py-2 border-t border-gray-200 bg-gray-50">
          <div class="text-xs text-gray-600">
            Showing {{ displayedRowCount }} of {{ stats.totalResults }} results
          </div>
        </div>
      </div>

      <!-- Right: Patient Drawer -->
      <transition
        enter-active-class="transition-all duration-300"
        leave-active-class="transition-all duration-300"
        enter-from-class="opacity-0 translate-x-4"
        leave-to-class="opacity-0 translate-x-4"
      >
        <div
          v-if="selectedPatient"
          class="w-1/2 flex flex-col overflow-hidden"
          style="height: calc(100vh - 250px)"
        >
          <!-- Patient Drawer Content -->
          <div class="flex-1 overflow-hidden">
            <Patient_Drawer
              :patient-identifier="selectedPatient.member_id"
              :entity-id="null"
              :patient-data="selectedPatientData"
              :patient-name="`${selectedPatient.first_name} ${selectedPatient.last_name}`"
              :enabled-tabs="['details', 'utilization', 'gaps', 'engagement']"
              default-tab="utilization"
              :show-header="true"
              :compact="true"
              @close="closeDetail"
            />
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { AgGridVue } from 'ag-grid-vue3';
import { themeQuartz } from 'ag-grid-community';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import Patient_Drawer from '@/components/Patient_Drawer/Patient_Drawer.vue';
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue';
import { beginSensitiveEgress } from '@/utils/demoEgress';
import demoProtectedFields from '@/mixins/demoProtectedFields';
import { sensitiveCell } from '@/utils/demoSpoof';

export default {
  name: 'Clinical_Results_List',
  components: {
    AgGridVue,
    FontAwesomeIcon,
    Patient_Drawer,
    ExportMenu,
  },
  mixins: [demoProtectedFields],
  // The patient-name search identifies real patients. Demo Mode empties it and the tagged
  // input is read-only while protected. It is not persisted.
  demoProtectedFields: ['filters.search'],
  data() {
    return {
      gridApi: null,
      loading: false,
      tableKey: 0,
      selectedPatient: null,
      displayedRowCount: 0,

      contractDropdownOpen: false,

      filters: {
        contractIds: [],
        measure: null,
        source: null,
        test: null,
        search: ''
      },

      contractOptions: [],
      testOptions: [],

      defaultColDef: {
        sortable: true,
        resizable: true,
        filter: false,
        cellClass: 'cell-theme-left-aligned',
        flex: 1
      },

      columnDefs: [
        {
          headerName: 'Contract',
          field: 'contract_id',
          minWidth: 90,
          cellClass: sensitiveCell('contract'),
        },
        {
          headerName: 'Patient',
          field: 'last_name',
          cellClass: 'cell-theme-left-aligned sensitive',
          cellRenderer: params => {
            if (!params.data) return '';
            const lastName = params.data.last_name || '';
            const firstName = params.data.first_name || '';
            return `<div class="sensitive font-medium">${lastName}, ${firstName}</div>`;
          },
          minWidth: 150
        },
        {
          headerName: 'MBI',
          field: 'bene_mbi_id',
          cellClass: 'sensitive',
          minWidth: 130
        },
        {
          headerName: 'DOB',
          field: 'dob',
          cellClass: 'sensitive',
          cellRenderer: params => {
            const value = params.value || params.data?.dob;
            if (!value) return '';
            return new Date(value).toLocaleDateString('en-US', {
              month: 'short', day: 'numeric', year: 'numeric'
            });
          },
          minWidth: 100
        },
        {
          headerName: 'Test',
          field: 'test_name',
          minWidth: 120
        },
        {
          headerName: 'LOINC',
          field: 'loinc_code',
          minWidth: 90
        },
        {
          headerName: 'Component',
          field: 'measure_component',
          minWidth: 100
        },
        {
          headerName: 'Result',
          field: 'result_value',
          cellRenderer: params => {
            if (!params.data) return '';
            const value = params.data.result_value ?? '';
            const units = params.data.units ?? '';
            const abnormal = params.data.abnormal_flag;
            const abnormalBadge = abnormal ?
              `<span class="ml-1 px-1 py-0.5 bg-red-100 text-red-700 text-xs rounded">${abnormal}</span>` : '';
            return `<span class="font-medium">${value}</span> <span class="text-gray-500 text-xs">${units}</span>${abnormalBadge}`;
          },
          minWidth: 100
        },
        {
          headerName: 'Date',
          field: 'date_of_service',
          cellRenderer: params => {
            const value = params.value || params.data?.date_of_service;
            if (!value) return '';
            // Parse as local date (not UTC) to avoid timezone shift
            const [y, m, d] = value.split('-');
            return new Date(y, m - 1, d).toLocaleDateString('en-US', {
              month: 'short', day: 'numeric', year: 'numeric'
            });
          },
          minWidth: 100
        },
        {
          headerName: 'Measure',
          field: 'quality_measure',
          cellRenderer: params => {
            const value = params.value || params.data?.quality_measure;
            if (!value) return '';
            const colors = {
              'CCS': 'bg-pink-100 text-pink-700',
              'CHL': 'bg-orange-100 text-orange-700',
              'CRC': 'bg-green-100 text-green-700',
              'GSD': 'bg-purple-100 text-purple-700',
              'HIV': 'bg-red-100 text-red-700',
              'KED': 'bg-blue-100 text-blue-700'
            };
            const colorClass = colors[value] || 'bg-gray-100 text-gray-700';
            return `<span class="px-2 py-0.5 ${colorClass} text-xs rounded">${value}</span>`;
          },
          minWidth: 80
        },
        {
          headerName: 'Source',
          field: 'lab_source',
          valueFormatter: params => {
            if (!params.value) return '';
            const names = {
              'ATHENA_EHR': 'Athena',
              'HEALTHJUMP_LAB': 'HealthJump',
              'LABCORP': 'LabCorp',
              'QUEST': 'Quest'
            };
            return names[params.value] || params.value;
          },
          minWidth: 90
        }
      ],

      gridOptions: {
        rowModelType: 'serverSide',
        suppressHorizontalScroll: false,
        animateRows: true,
        enableCellTextSelection: true,
        ensureDomOrder: true,
        autoSizeStrategy: {
          type: 'fitGridWidth'
        }
      },

      rowClassRules: {
        'bg-blue-50': params => params.data?.member_id === this.selectedPatient?.member_id
      },

      gridTheme: themeQuartz.withParams({
        browserColorScheme: 'light',
        backgroundColor: '#ffffff',
        foregroundColor: '#1f2937',
        borderColor: '#e5e7eb',
        borderRadius: 0,
        fontFamily: 'inherit',
        fontSize: '14px',
        headerBackgroundColor: '#f9fafb',
        headerFontSize: '12px',
        headerFontWeight: 600,
        oddRowBackgroundColor: '#ffffff',
        rowBorder: true,
        rowHoverColor: '#f3f4f6',
        spacing: 8,
        wrapperBorder: false
      }),

      stats: {
        totalResults: 0
      }
    };
  },

  computed: {
    selectedPatientData() {
      if (!this.selectedPatient) return null;
      return {
        bene_first_name: this.selectedPatient.first_name,
        bene_last_name: this.selectedPatient.last_name,
        bene_dob: this.selectedPatient.dob
      };
    }
  },

  async mounted() {
    await this.loadContractOptions();
    document.addEventListener('click', this.handleClickOutside);
  },

  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  },

  methods: {
    getRowId(params) {
      // Use combination of member_id + loinc + date for unique row ID
      if (!params.data) return Math.random().toString();
      return `${params.data.member_id || ''}-${params.data.loinc_code || ''}-${params.data.date_of_service || ''}-${Math.random()}`;
    },

    async loadContractOptions() {
      try {
        // Get distinct contracts and tests from clinical results data
        const response = await this.$axios.post('/api/clinical/results-list/', {
          start_row: 0,
          end_row: 1,
          get_filters: true
        });
        if (response.data?.filters?.contracts) {
          this.contractOptions = response.data.filters.contracts.map(c => ({
            value: c,
            label: c
          }));
        }
        if (response.data?.filters?.tests) {
          this.testOptions = response.data.filters.tests.sort();
        }
      } catch (err) {
        console.error('Error loading filters:', err);
      }
    },

    async onGridReady(params) {
      this.gridApi = params.api;

      const datasource = {
        getRows: async (paramsRequest) => {
          try {
            this.loading = true;

            const response = await this.$axios.post('/api/clinical/results-list/', {
              start_row: paramsRequest.request.startRow,
              end_row: paramsRequest.request.endRow,
              contract_ids: this.filters.contractIds.length ? this.filters.contractIds : undefined,
              measure: this.filters.measure,
              source: this.filters.source,
              test_name: this.filters.test,
              search: this.filters.search
            });

            if (response.data?.success) {
              this.stats.totalResults = response.data.lastRow || 0;
              this.displayedRowCount = response.data.rowData?.length || 0;

              paramsRequest.success({
                rowData: response.data.rowData,
                rowCount: response.data.lastRow
              });
            } else {
              paramsRequest.fail();
            }
          } catch (err) {
            console.error('Error fetching results:', err);
            paramsRequest.fail();
          } finally {
            this.loading = false;
          }
        }
      };

      this.gridApi.setGridOption('serverSideDatasource', datasource);
    },

    refreshGrid() {
      if (this.gridApi) {
        this.gridApi.refreshServerSide({ purge: true });
      }
    },

    onRowClicked(event) {
      if (event.data) {
        this.selectedPatient = event.data;
      }
    },

    closeDetail() {
      this.selectedPatient = null;
    },

    handleClickOutside(event) {
      if (this.$refs.contractDropdown && !this.$refs.contractDropdown.contains(event.target)) {
        this.contractDropdownOpen = false;
      }
    },

    async exportData() {
      const ticket = beginSensitiveEgress('export');
      if (!ticket) return;
      try {
        // Fetch all results matching current filters
        const response = await this.$axios.post('/api/clinical/results-list/', {
          start_row: 0,
          end_row: this.stats.totalResults || 100000,
          contract_ids: this.filters.contractIds.length ? this.filters.contractIds : undefined,
          measure: this.filters.measure,
          source: this.filters.source,
          test_name: this.filters.test,
          search: this.filters.search,
          export: true
        });

        if (!response.data?.success || !response.data?.rowData?.length) {
          console.error('No data to export');
          return;
        }

        const rows = response.data.rowData;

        // Define columns for export
        const columns = [
          { field: 'contract_id', header: 'Contract' },
          { field: 'last_name', header: 'Last Name' },
          { field: 'first_name', header: 'First Name' },
          { field: 'bene_mbi_id', header: 'MBI' },
          { field: 'dob', header: 'DOB' },
          { field: 'test_name', header: 'Test' },
          { field: 'loinc_code', header: 'LOINC' },
          { field: 'measure_component', header: 'Component' },
          { field: 'result_value', header: 'Result' },
          { field: 'units', header: 'Units' },
          { field: 'abnormal_flag', header: 'Abnormal Flag' },
          { field: 'date_of_service', header: 'Date' },
          { field: 'quality_measure', header: 'Measure' },
          { field: 'lab_source', header: 'Source' }
        ];

        // Build CSV
        const headerRow = columns.map(c => c.header).join(',');
        const dataRows = rows.map(row => {
          return columns.map(c => {
            let val = row[c.field] ?? '';
            // Escape quotes and wrap in quotes if contains comma
            if (typeof val === 'string' && (val.includes(',') || val.includes('"'))) {
              val = '"' + val.replace(/"/g, '""') + '"';
            }
            return val;
          }).join(',');
        });

        const csv = [headerRow, ...dataRows].join('\n');

        // Download
        if (!ticket.confirm()) return;
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = 'clinical_results.csv';
        link.click();
        URL.revokeObjectURL(link.href);
      } catch (err) {
        console.error('Error exporting data:', err);
      }
    }
  }
};
</script>

<style scoped>
/* AG Grid cell styling - wrap text within cell bounds */
:deep(.ag-cell) {
  display: flex;
  align-items: center;
  overflow: hidden;
}

:deep(.ag-cell-value) {
  white-space: normal;
  word-wrap: break-word;
  overflow-wrap: break-word;
  line-height: 1.3;
  overflow: hidden;
}
</style>
